import React from 'react';
import { Checkbox, mergeStyleSets, Stack } from '@fluentui/react';
import { deepEqual } from 'fast-equals';
import Utils from '../../../shared/Utils';
import ChartDefaults from '../../../shared/ChartDefaults';
import GraphDisplayLayer from './GraphDisplayLayer';
import ModellerDefaults from '../../ModellerDefaults';
import GraphUtils from '../../GraphUtils';
import CytoReducer from './CytoReducer';
import Store from '../../../shared/state/Store';
import ModellerReducer from '../../ModellerReducer';

const componentStyles = mergeStyleSets({
  container: {
    width: '100%',
  },
});

const getColor = (group) => ChartDefaults.Colors[group] || 'gray';

export default ({ graphKey }) => {
  const [globalState, dispatch] = Store.useStore();
  const model = ModellerReducer.getModel(globalState);
  const [chartData, setChartData] = React.useState();

  const isNetworkView = graphKey !== ModellerDefaults.FlatModelGraphKey && graphKey !== ModellerDefaults.ModelViewGraphKey;
  const totalNodeCount = (model?.networks || []).reduce((sum, network) => sum + (network.nodes?.length || 0), 0);
  const isFlatModelGraph = graphKey === ModellerDefaults.FlatModelGraphKey;
  const isFlatModelGraphAllowed = !isFlatModelGraph || totalNodeCount < ModellerDefaults.FlatViewThreshold;

  const modelViewMode = globalState.config?.generalConfig?.modelViewMode;
  const modelViewCustomPermissions = modelViewMode === 'custom' ? (globalState.config?.generalConfig?.modelViewCustomPermissions ?? {}) : {};
  const showHiddenNodes = modelViewCustomPermissions.showHiddenNodes || false;

  const prepareData = (json) => {
    const fChartData = {
      elements: {
        nodes: [],
        edges: [],
      },
      style: [],
    };

    // Handle model view graph - shows networks as nodes with input/output ports
    if (graphKey === ModellerDefaults.ModelViewGraphKey) {
      if (json.graphics?.graph) {
        // Use graph graphics for model view (zoom/pan)
        // Create a copy to avoid mutating the original
        const graphGraphics = { ...json.graphics.graph };
        // Ensure style is an array
        if (!Array.isArray(graphGraphics.style)) {
          graphGraphics.style = [];
        }
        Object.assign(fChartData, graphGraphics);
      }

      if (!json.networks || json.networks.length <= 1) {
        return fChartData; // Model view only makes sense with multiple networks
      }

      // Safe separator (network ids can contain '-' etc.)
      const SEP = '|';

      // Build nodeByNetworkAndId map for link processing
      const nodeByNetworkAndId = new Map();
      json.networks.forEach((n) => {
        (n.nodes || []).forEach((node) => {
          nodeByNetworkAndId.set(`${n.id}${SEP}${node.id}`, node);
        });
      });

      // Precompute inter-network IO edges (output->input only, different networks)
      const interNetworkEdges = [];
      if (json.links) {
        json.links.forEach((link) => {
          const sourceNode = nodeByNetworkAndId.get(`${link.sourceNetwork}${SEP}${link.sourceNode}`);
          const targetNode = nodeByNetworkAndId.get(`${link.targetNetwork}${SEP}${link.targetNode}`);
          const isSourceOutput = sourceNode?.configuration?.output === true;
          const isTargetInput = targetNode?.configuration?.input === true;

          if (isSourceOutput && isTargetInput && link.sourceNetwork !== link.targetNetwork) {
            interNetworkEdges.push({
              sourceNetwork: link.sourceNetwork,
              sourceNode: link.sourceNode,
              targetNetwork: link.targetNetwork,
              targetNode: link.targetNode,
            });
          }
        });
      }

      // Build per-network IO lists with original indices
      const inputNodesByNetwork = new Map();
      const outputNodesByNetwork = new Map();

      json.networks.forEach((network) => {
        const inputList = [];
        const outputList = [];

        if (network.nodes) {
          network.nodes.forEach((node, nodeIndex) => {
            const isInput = node.configuration?.input === true;
            const isOutput = node.configuration?.output === true;

            if (isInput) {
              inputList.push({ node, origIndex: nodeIndex });
            }
            if (isOutput) {
              outputList.push({ node, origIndex: nodeIndex });
            }
          });
        }

        inputNodesByNetwork.set(network.id, inputList);
        outputNodesByNetwork.set(network.id, outputList);
      });

      // Count inter-network crossings
      const countInterNetworkCrossings = () => {
        // Build index maps for fast lookup
        const inputIndexMap = new Map();
        const outputIndexMap = new Map();

        json.networks.forEach((network) => {
          const inputMap = new Map();
          const outputMap = new Map();
          const inputList = inputNodesByNetwork.get(network.id) || [];
          const outputList = outputNodesByNetwork.get(network.id) || [];

          inputList.forEach((item, index) => {
            inputMap.set(item.node.id, index);
          });
          outputList.forEach((item, index) => {
            outputMap.set(item.node.id, index);
          });

          inputIndexMap.set(network.id, inputMap);
          outputIndexMap.set(network.id, outputMap);
        });

        // Group edges by (sourceNetwork -> targetNetwork) pairs
        const edgesByPair = new Map();
        interNetworkEdges.forEach((edge) => {
          const pairKey = `${edge.sourceNetwork}->${edge.targetNetwork}`;
          if (!edgesByPair.has(pairKey)) {
            edgesByPair.set(pairKey, []);
          }
          edgesByPair.get(pairKey).push(edge);
        });

        let totalCrossings = 0;

        // Count inversions for each network pair
        edgesByPair.forEach((edges, pairKey) => {
          if (edges.length < 2) return; // No crossings possible with < 2 edges

          // Get source and target network IDs
          const [sourceNetworkId, targetNetworkId] = pairKey.split('->');
          const sourceOutputMap = outputIndexMap.get(sourceNetworkId);
          const targetInputMap = inputIndexMap.get(targetNetworkId);

          if (!sourceOutputMap || !targetInputMap) return;

          // Build list of (sourceIndex, targetIndex) pairs
          const pairs = edges.map((edge) => {
            const sourceIndex = sourceOutputMap.get(edge.sourceNode);
            const targetIndex = targetInputMap.get(edge.targetNode);
            if (sourceIndex === undefined || targetIndex === undefined) return null;
            return { sourceIndex, targetIndex };
          }).filter((p) => p !== null);

          // Count inversions: (e1.sourceIndex - e2.sourceIndex) * (e1.targetIndex - e2.targetIndex) < 0
          for (let i = 0; i < pairs.length; i += 1) {
            for (let j = i + 1; j < pairs.length; j += 1) {
              const a = pairs[i];
              const b = pairs[j];
              const sourceDiff = a.sourceIndex - b.sourceIndex;
              const targetDiff = a.targetIndex - b.targetIndex;
              if (sourceDiff * targetDiff < 0) {
                totalCrossings += 1;
              }
            }
          }
        });

        return totalCrossings;
      };

      // Optimize using adjacent-swap hill-climbing
      const optimizeWithAdjacentSwaps = () => {
        const MAX_PASSES = 10;
        let currentCrossings = countInterNetworkCrossings();

        for (let pass = 0; pass < MAX_PASSES; pass += 1) {
          let improved = false;

          // Optimize inputs for each network
          for (let netIdx = 0; netIdx < json.networks.length; netIdx += 1) {
            const network = json.networks[netIdx];
            const inputList = inputNodesByNetwork.get(network.id) || [];
            if (inputList.length < 2) continue;

            // Try adjacent swaps
            for (let i = 0; i < inputList.length - 1; i += 1) {
              // Try swapping i and i+1
              const newList = [...inputList];
              [newList[i], newList[i + 1]] = [newList[i + 1], newList[i]];

              // Temporarily update to test crossings
              inputNodesByNetwork.set(network.id, newList);
              const crossingsAfter = countInterNetworkCrossings();
              inputNodesByNetwork.set(network.id, inputList); // Restore

              if (crossingsAfter < currentCrossings) {
                // Accept swap
                inputNodesByNetwork.set(network.id, newList);
                currentCrossings = crossingsAfter;
                improved = true;
                // Update inputList reference for next iteration
                inputList[i] = newList[i];
                inputList[i + 1] = newList[i + 1];
              }
            }
          }

          // Optimize outputs for each network
          for (let netIdx = 0; netIdx < json.networks.length; netIdx += 1) {
            const network = json.networks[netIdx];
            const outputList = outputNodesByNetwork.get(network.id) || [];
            if (outputList.length < 2) continue;

            // Try adjacent swaps
            for (let i = 0; i < outputList.length - 1; i += 1) {
              // Try swapping i and i+1
              const newList = [...outputList];
              [newList[i], newList[i + 1]] = [newList[i + 1], newList[i]];

              // Temporarily update to test crossings
              outputNodesByNetwork.set(network.id, newList);
              const crossingsAfter = countInterNetworkCrossings();
              outputNodesByNetwork.set(network.id, outputList); // Restore

              if (crossingsAfter < currentCrossings) {
                // Accept swap
                outputNodesByNetwork.set(network.id, newList);
                currentCrossings = crossingsAfter;
                improved = true;
                // Update outputList reference for next iteration
                outputList[i] = newList[i];
                outputList[i + 1] = newList[i + 1];
              }
            }
          }

          if (!improved) {
            break;
          }
        }
      };

      // Apply optimization
      optimizeWithAdjacentSwaps();

      // Create network nodes as compound nodes
      json.networks.forEach((network) => {
        const networkNodeId = `network_${network.id}`;

        // Load position from network.graphics.graph.position if it exists
        const networkGraphics = network.graphics?.graph;
        const savedPosition = networkGraphics?.position;
        const networkWidth = networkGraphics?.size?.width
          || networkGraphics?.width
          || ModellerDefaults.DefaultGraphics.graph.width
          || 200;

        // Get reordered input and output nodes
        const inputList = inputNodesByNetwork.get(network.id) || [];
        const outputList = outputNodesByNetwork.get(network.id) || [];
        const inputNodes = inputList.map((item) => item.node);
        const outputNodes = outputList.map((item) => item.node);

        // Size network node to fit header + ports (avoid unused vertical space)
        const nameHeaderHeight = 30;
        const baseNodeHeight = 25;
        const baseNodeSpacing = 2;
        const portCount = inputNodes.length + outputNodes.length;
        const basePortSectionHeight = portCount > 0
          ? (portCount * baseNodeHeight) + ((portCount + 1) * baseNodeSpacing)
          : baseNodeSpacing;
        const explicitHeight = networkGraphics?.size?.height || networkGraphics?.height;
        const hasExplicitHeight = typeof explicitHeight === 'number' && explicitHeight > 0;

        let nodeHeight = baseNodeHeight;
        let nodeSpacing = baseNodeSpacing;
        let networkHeight = nameHeaderHeight + basePortSectionHeight;

        if (hasExplicitHeight) {
          networkHeight = explicitHeight;
          if (portCount > 0 && explicitHeight > nameHeaderHeight) {
            const availablePortHeight = explicitHeight - nameHeaderHeight;
            const scale = availablePortHeight / basePortSectionHeight;
            nodeHeight = baseNodeHeight * scale;
            nodeSpacing = baseNodeSpacing * scale;
          }
        }

        const networkNode = {
          selectable: true,
          grabbable: true,
          pannable: false,
          data: {
            id: networkNodeId,
            name: network.name || network.id,
            networkId: network.id,
            type: 'network',
            width: networkWidth,
            height: networkHeight,
          },
          classes: ['network-node'],
        };

        // Apply saved position if it exists and is valid (not 0,0 for multiple networks)
        // For a single network, 0,0 might be valid, but for multiple networks it's likely invalid
        if (savedPosition
          && typeof savedPosition.x === 'number'
          && typeof savedPosition.y === 'number'
          && (json.networks.length === 1 || savedPosition.x !== 0 || savedPosition.y !== 0)) {
          networkNode.position = { x: savedPosition.x, y: savedPosition.y };
        }

        fChartData.elements.nodes.push(networkNode);

        // Network name header (child of network node)
        const networkNameId = `${networkNodeId}_name`;
        fChartData.elements.nodes.push({
          selectable: false,
          grabbable: false,
          pannable: false,
          data: {
            id: networkNameId,
            parent: networkNodeId,
            name: network.name || network.id,
            type: 'network-name',
            width: Math.max(0, networkWidth - 2),
            height: 30,
          },
          classes: ['network-name'],
          position: { x: 0, y: (-networkHeight / 2) + (nameHeaderHeight / 2) },
        });

        // Add input nodes - position them below the name header
        const startY = (-networkHeight / 2) + nameHeaderHeight + nodeSpacing + (nodeHeight / 2);

        inputNodes.forEach((node, index) => {
          const inputNodeId = `${networkNodeId}_input_${node.id}`;
          const inputY = startY + (index * (nodeHeight + nodeSpacing));
          const portWidth = Math.max(0, networkWidth - 2);

          // Create the input node
          fChartData.elements.nodes.push({
            selectable: false,
            grabbable: false,
            pannable: false,
            data: {
              id: inputNodeId,
              parent: networkNodeId,
              name: node.name || node.id,
              networkId: network.id,
              nodeId: node.id,
              type: 'input',
              width: portWidth,
              height: nodeHeight,
            },
            classes: ['input-node'],
            position: { x: 0, y: inputY },
          });

          // Create triangle icon as separate node positioned outside on the left
          fChartData.elements.nodes.push({
            selectable: false,
            grabbable: false,
            pannable: false,
            data: {
              id: `${inputNodeId}_icon`,
              portId: inputNodeId,
              networkId: network.id,
              side: 'left',
              type: 'input-icon',
            },
            classes: ['input-icon'],
            position: { x: 0, y: inputY },
          });
        });

        // Add output nodes - position them after input nodes
        const outputStartY = startY + (inputNodes.length * (nodeHeight + nodeSpacing));
        outputNodes.forEach((node, index) => {
          const outputNodeId = `${networkNodeId}_output_${node.id}`;
          const outputY = outputStartY + (index * (nodeHeight + nodeSpacing));
          const portWidth = Math.max(0, networkWidth - 2);

          // Create the output node
          fChartData.elements.nodes.push({
            selectable: false,
            grabbable: false,
            pannable: false,
            data: {
              id: outputNodeId,
              parent: networkNodeId,
              name: node.name || node.id,
              networkId: network.id,
              nodeId: node.id,
              type: 'output',
              width: portWidth,
              height: nodeHeight,
            },
            classes: ['output-node'],
            position: { x: 0, y: outputY },
          });

          // Create half-circle icon as separate node positioned outside on the right
          fChartData.elements.nodes.push({
            selectable: false,
            grabbable: false,
            pannable: false,
            data: {
              id: `${outputNodeId}_icon`,
              portId: outputNodeId,
              networkId: network.id,
              side: 'right',
              type: 'output-icon',
            },
            classes: ['output-icon'],
            position: { x: 0, y: outputY },
          });
        });
      });

      // Create edges based on model.links
      // Track inter-network connections to create SYNTHETIC network-level edges (for initial layout only)
      const interNetworkConnections = new Set();

      if (json.links) {
        json.links.forEach((link) => {
          const sourceNetworkId = `network_${link.sourceNetwork}`;
          const targetNetworkId = `network_${link.targetNetwork}`;

          // Find source output node
          const sourceNode = nodeByNetworkAndId.get(`${link.sourceNetwork}${SEP}${link.sourceNode}`);
          const isSourceOutput = sourceNode?.configuration?.output === true;

          // Find target input node
          const targetNode = nodeByNetworkAndId.get(`${link.targetNetwork}${SEP}${link.targetNode}`);
          const isTargetInput = targetNode?.configuration?.input === true;

          if (isSourceOutput && isTargetInput) {
            // Connect from output icon to input icon
            const sourceIconId = `${sourceNetworkId}_output_${link.sourceNode}_icon`;
            const targetIconId = `${targetNetworkId}_input_${link.targetNode}_icon`;

            // REAL edge between icon nodes
            fChartData.elements.edges.push({
              data: {
                id: `${sourceIconId}-${targetIconId}`,
                source: sourceIconId,
                target: targetIconId,
              },
            });

            // Track derived inter-network connection (only if different networks)
            if (link.sourceNetwork !== link.targetNetwork) {
              // Use safe SEP so ids containing '-' don't break split()
              interNetworkConnections.add(`${sourceNetworkId}${SEP}${targetNetworkId}`);
            }
          }
        });
      }

      // Create SYNTHETIC inter-network edges (one per network pair)
      // These should be used ONLY for initial layout and then removed from the Cytoscape instance.
      interNetworkConnections.forEach((connectionKey) => {
        const [sourceNetworkId, targetNetworkId] = connectionKey.split(SEP);

        fChartData.elements.edges.push({
          data: {
            id: `__synthetic__${sourceNetworkId}=>${targetNetworkId}`,
            source: sourceNetworkId,
            target: targetNetworkId,
            synthetic: true,
          },
          classes: ['inter-network-edge', 'synthetic'],
        });
      });

      return fChartData;
    }

    if (graphKey === ModellerDefaults.FlatModelGraphKey && json.graphics?.cyto) {
      // console.log('Merging model graphics:', json.graphics.cyto);
      Object.assign(fChartData, json.graphics.cyto);
    }

    const linkedNodes = {};
    const skipNodes = {}; // Do not show this nodes (used for hidden nodes when showHiddenNodes is false)
    const skipLinks = []; // Link skips are used to replace links to hidden nodes with links to their non-hidden descendants

    if (json && json.networks) {
      for (let netIndex = 0; netIndex < json.networks.length; netIndex += 1) {
        const network = json.networks[netIndex];
        linkedNodes[network.id] = {};
        skipNodes[network.id] = {};

        const networkGraphKey = GraphUtils.escapeSelector(network.id);

        if (isNetworkView && networkGraphKey !== graphKey) {
          continue;
        }

        if (graphKey === networkGraphKey && network.graphics?.cyto) {
          // console.log('Merging network graphics:', network.graphics.cyto);
          Object.assign(fChartData, network.graphics.cyto);
        }

        if (network.nodes) {
          for (let nodeIndex = 0; nodeIndex < json.networks[netIndex].nodes.length; nodeIndex += 1) {
            const node = json.networks[netIndex].nodes[nodeIndex];
            const netNodeKey = Utils.createNetworkNodeKey(network.id, node.id);
            const nodeGraphics = node.graphics?.graph || ModellerDefaults.DefaultGraphics.graph;
            const graphicsClasses = (nodeGraphics.classes || []);
            const graphicsExtraDisplayModeClass = graphicsClasses.includes(`mode_${ModellerDefaults.NodeModes.CHART}`) ? '' : `mode_${ModellerDefaults.NodeModes.PLAIN}`;

            linkedNodes[network.id][node.id] = {
              id: node.id,
              linksOut: [],
            };
            const isHidden = node.graphics?.visible === false;
            if (isHidden && !showHiddenNodes) {
              skipNodes[network.id][node.id] = true;
              continue;
            }

            const continuous = ['ContinuousInterval', 'IntegerInterval'].includes(node.configuration.type);
            const elementData = {
              data: {
                selector: GraphUtils.escapeSelector(netNodeKey),
                id: netNodeKey,
                nodeId: node.id,
                name: node.name,
                networkId: network.id,
                continuous,
                discrete: !continuous,
                parent: `${netNodeKey}wrapper`,
              },
              classes: [...graphicsClasses, graphicsExtraDisplayModeClass, 'primary', ...(isHidden && showHiddenNodes ? ['hidden-to-show'] : [])],
              selected: nodeGraphics.selected || false,
              position: nodeGraphics.position?.[graphKey],
            };
            // if (node.graphics?.cyto?.mode === ModellerDefaults.NodeModes.CHART) {
            //   elementData.classes.push('mode_chart');
            //   // chartData.style.push({
            //   //   selector: `[name=${netNodeKey}]`,
            //   //   style: {
            //   //     shape: 'rectangle',
            //   //     'background-image': `url('${example.chartUrlSvg}')`,
            //   //     'background-repeat': 'no-repeat',
            //   //     'background-width-relative-to': 'inner',
            //   //     'background-height-relative-to': 'inner',
            //   //     // 'background-size': '100%',
            //   //     'background-width': (elNode) => `${elNode.width()}px`,
            //   //     'background-height': (elNode) => `${elNode.height()}px`,
            //   //     // 'background-clip': 'node',
            //   //   },
            //   // });
            // }

            fChartData.elements.nodes.push(elementData);

            // Add a wrapper compound node that will hold the actual node and observation labels
            fChartData.elements.nodes.push({
              selectable: false,
              grabbable: false,
              pannable: true,
              data: {
                id: `${netNodeKey}wrapper`,
                name: '',
              },
              classes: ['wrapper'],
            });

            model.dataSets.forEach((ds, dsIndex) => {
              const mappedObservations = globalState.modeller?.mappedObservations?.[ds.id]?.[network.id]?.[node.id] || {};
              Object.keys(mappedObservations).forEach((obsKey) => {
                const obs = mappedObservations[obsKey];
                const labelValue = (obs.entries.length === 1) ? obs.entries[0].value : 'Soft Evidence';
                const objId = GraphUtils.escapeSelector(ds.id + network.id + node.id + obsKey);
                fChartData.elements.nodes.push({
                  selectable: false,
                  grabbable: false,
                  pannable: true,
                  data: {
                    parent: `${netNodeKey}wrapper`,
                    id: objId,
                    name: `${ds.id}: ${labelValue}${(obsKey && obsKey !== 'undefined') ? ` (${obsKey})` : ''}`,
                    dsIndex,
                    color: getColor(dsIndex),
                  },
                  classes: ['observation', 'hidden'],
                });
              });
            });

            const nodeStyle = {
              selector: `[selector="${GraphUtils.escapeSelector(netNodeKey)}"]`,
              style: {},
            };

            if (Object.keys(nodeGraphics.style || {}).length > 0) {
              nodeStyle.style = {
                ...nodeStyle.style,
                ...nodeGraphics.style,
                custom: undefined,
              };

              if (nodeGraphics.style.custom) {
                Object.keys(nodeGraphics.style.custom).forEach((customClass) => {
                  fChartData.style.push({
                    selector: `[selector="${GraphUtils.escapeSelector(netNodeKey)}"].${customClass}`,
                    style: nodeGraphics.style.custom[customClass],
                  });
                });
              }
            }

            if (graphKey === ModellerDefaults.FlatModelGraphKey && ModellerReducer.getSetting(globalState, ModellerReducer.SettingKeys.colorByNetwork)) {
              nodeStyle.style['background-color'] = getColor(netIndex);
            }

            fChartData.style.push(nodeStyle);
          }
        }

        if (network.links) {
          network.links.forEach((link) => {
            linkedNodes[network.id][link.parent].linksOut.push({
              network: network.id,
              node: link.child,
            });

            if (skipNodes[network.id][link.parent]) {
              // Source is hidden, discard this link
              return;
            }
            if (skipNodes[network.id][link.child]) {
              // The target is hidden, make this a skip link
              skipLinks[network.id] = skipLinks.push({
                sourceNetwork: network.id,
                sourceNode: link.parent,
                targetNetwork: network.id,
                targetNode: link.child,
                value: 2,
              });
              return;
            }
            const source = `${network.id}.${link.parent}`;
            const target = `${network.id}.${link.child}`;
            fChartData.elements.edges.push({
              data: {
                id: `${source}-${target}`,
                source,
                target,
                value: 2,
              },
            });
          });
        }
      }
    }

    if (!isNetworkView && json && json.links) {
      json.links.forEach((link) => {
        // Build a linked graph of nodes so we can replace links to hidden nodes with links to their non-hidden descendants
        linkedNodes[link.sourceNetwork][link.sourceNode].linksOut.push({
          network: link.targetNetwork,
          node: link.targetNode,
        });

        if (skipNodes[link.sourceNetwork][link.sourceNode]) {
          // Source is hidden, discard this link
          return;
        }
        if (skipNodes[link.targetNetwork][link.targetNode]) {
          // The target is hidden, make this a skip link
          skipLinks.push({
            sourceNetwork: link.sourceNetwork,
            sourceNode: link.sourceNode,
            targetNetwork: link.targetNetwork,
            targetNode: link.targetNode,
            value: 1,
          });
          return;
        }
        const source = `${link.sourceNetwork}.${link.sourceNode}`;
        const target = `${link.targetNetwork}.${link.targetNode}`;
        fChartData.elements.edges.push({
          data: {
            id: `${source}-${target}`,
            source,
            target,
            value: 1,
          },
        });
      });
    }

    while (skipLinks.length > 0) {
      const linkData = skipLinks[0];
      // const targetNetNodeKey = Utils.createNetworkNodeKey(linkData.targetNetwork, linkData.targetNode);
      if (!skipNodes[linkData.targetNetwork]?.[linkData.targetNode]) {
        // Target node not hidden, we can move this link to actual links
        const source = `${linkData.sourceNetwork}.${linkData.sourceNode}`;
        const target = `${linkData.targetNetwork}.${linkData.targetNode}`;
        fChartData.elements.edges.push({
          data: {
            id: `${source}-${target}`,
            source,
            target,
            value: 1,
          },
          classes: ['dashed'],
        });
      } else {
        // Target node is hidden, need to replace this link with a skip link per each of its outgoing links
        const targetNode = linkedNodes[linkData.targetNetwork][linkData.targetNode];
        (targetNode.linksOut || []).forEach((linkOut) => {
          skipLinks.push({
            sourceNetwork: linkData.sourceNetwork,
            sourceNode: linkData.sourceNode,
            targetNetwork: linkOut.network,
            targetNode: linkOut.node,
            value: linkData.value,
          });
        });
      }
      skipLinks.shift();
    }

    return fChartData;
  };

  // const updateNode = ({ updateBatch, updatePath, mergeUpdate }) => {
  //   const modelCopy = Utils.clone(model);
  //   updateBatch.forEach((row) => {
  //     const { networkId, nodeId, updateBody } = row;

  //     // eslint-disable-next-line no-restricted-syntax, no-labels
  //     loop:
  //     for (let netIndex = 0; netIndex < modelCopy.networks.length; netIndex += 1) {
  //       const net = modelCopy.networks[netIndex];
  //       if (net.id === networkId) {
  //         for (let nodeIndex = 0; nodeIndex < modelCopy.networks[netIndex].nodes.length; nodeIndex += 1) {
  //           const node = modelCopy.networks[netIndex].nodes[nodeIndex];
  //           if (node.id === nodeId) {
  //             const oldNode = modelCopy.networks[netIndex].nodes[nodeIndex];
  //             if (mergeUpdate) {
  //               const oldBody = Utils.resolveObjectPath(oldNode, updatePath, {});
  //               const newBody = { ...oldBody, ...updateBody };
  //               Utils.setObjectPath(oldNode, updatePath, newBody);
  //               console.log(oldBody, updateBody, newBody, oldNode);
  //               modelCopy.networks[netIndex].nodes[nodeIndex] = oldNode;
  //             } else {
  //               Utils.setObjectPath(oldNode, updatePath, updateBody);
  //               modelCopy.networks[netIndex].nodes[nodeIndex] = oldNode;
  //             }
  //             // eslint-disable-next-line no-restricted-syntax, no-labels
  //             break loop;
  //           }
  //         }
  //       }
  //     }
  //   });

  //   updateModel(modelCopy);
  // };

  // const updateNodeGraphics = (updateBatch) => {
  //   const preparedBatch = [];
  //   updateBatch.forEach((row) => {
  //     preparedBatch.push(row);
  //   });
  //   updateNode({ updateBatch: preparedBatch, mergeUpdate: true, updatePath: 'graphics.cyto' });
  // };

  // const updateModelGraphics = (updateBody, mergeUpdate = false) => {
  //   const modelCopy = Utils.clone(model);
  //   modelCopy.graphics = modelCopy.graphics || {};
  //   modelCopy.graphics.cyto = modelCopy.graphics.cyto || {};
  //   if (mergeUpdate) {
  //     modelCopy.graphics.cyto = {
  //       ...modelCopy.graphics.cyto,
  //       ...updateBody,
  //     };
  //   }
  //   updateModel(modelCopy);
  // };

  React.useEffect(() => {
    // if (chartData && !CytoReducer.isRepaintRequested(globalState) && ModellerReducer.getMappedGraph(globalState, graphKey)) {
    //   return;
    // }
    if (!isFlatModelGraphAllowed) {
      if (chartData) {
        setChartData(undefined);
      }
      return;
    }
    const preChartData = prepareData(model);
    if (!deepEqual(chartData, preChartData)) {
      setChartData(preChartData);
    }
  },
  [model, graphKey, isFlatModelGraphAllowed, CytoReducer.isRepaintRequested(globalState), globalState.modeller?.mappedResults]);

  if (!model || !model.networks) {
    return <div><p>No valid model</p></div>;
  }

  if (!isFlatModelGraphAllowed) {
    return <div><p>Model (merged) view is disabled for models with {ModellerDefaults.FlatViewThreshold} or more nodes.</p></div>;
  }

  if (!chartData || Object.values(chartData).length === 0) {
    return <div><p>No valid chart data</p></div>;
  }

  // // ////////////////
  // chartData.style.push(
  //   {
  //     selector: `[selector="${GraphUtils.escapeSelector('River Flooding T2_2.Flood')}"]`,
  //     style: {
  //       'background-color': 'lime',
  //     },
  //   },
  // );
  // // ////////////////

  // console.log('cyto preview', cyto);

  return (
    <Stack vertical className={componentStyles.container}>
      {/* {
        cyto && (
          <p>
            {
              cyto.$('node:selected').map((node) => (
                <span key={node.id()}>
                  {node.data().name}
                  ,
                  {' '}
                </span>
              ))
              // cyto.nodes().map((node) => <span>{node.data().name}</span>)
            }
          </p>
        )
      } */}

      {
        (graphKey === ModellerDefaults.FlatModelGraphKey) && (
          <Checkbox
            label="Color by network"
            checked={ModellerReducer.getSetting(globalState, ModellerReducer.SettingKeys.colorByNetwork)}
            onChange={async () => {
              dispatch(ModellerReducer.setSetting(ModellerReducer.SettingKeys.colorByNetwork, !ModellerReducer.getSetting(globalState, ModellerReducer.SettingKeys.colorByNetwork)));
              dispatch(CytoReducer.setRepaintRequested());
            }}
          />
        )
      }

      <GraphDisplayLayer
        data={chartData}
        graphKey={graphKey}
      />

    </Stack>
  );
};
